import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, List, ListItem, ListItemText, TextField } from '@mui/material';
import { motion } from 'framer-motion';
import PropertyForm from './PropertyForm';
import PropertyCard from './PropertyCard';
import UserForm from './UserForm';
import UserTable from './UserTable';
import ReviewPanel from './ReviewPanel';
import AnalyticsDashboard from '../pages/AnalyticsDashboard';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:7071'}/api/property`;

export default function PropertyPanel() {
  const [property, setProperty] = useState(null);
  const [open, setOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [section, setSection] = useState('propertyInfo');
  const [users, setUsers] = useState([]);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({ images: [] });
  const [rooms, setRooms] = useState([]);
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [selectedImageIndexes, setSelectedImageIndexes] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);

  const SkeletonBlock = ({ style = {} }) => (
    <motion.div
      initial={{ backgroundPositionX: '100%' }}
      animate={{ backgroundPositionX: '-100%' }}
      transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
      style={{
        width: '100%',
        height: '20px',
        borderRadius: '6px',
        backgroundImage: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
        backgroundSize: '200% 100%',
        backgroundRepeat: 'no-repeat',
        marginBottom: '12px',
        ...style
      }}
    />
  );

  const fetchProperty = async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    const propertyId = user?.propertyId?.id || user?.properties?.[0]?.propertyId?.id;
    if (!propertyId) {
      return;
    }

    const res = await fetch(`${API_URL}?propertyId=${propertyId}`);
    const data = await res.json();
    setProperty(data);
    setFormData(prev => ({ ...prev, images: data.images || [], googleMapLink: data.googleMapLink || "" }));
  };

  const fetchUsers = async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    const propertyId = user?.propertyId?.id || user?.properties?.[0]?.propertyId?.id;
    if (!propertyId) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/users?propertyId=${propertyId}`);
      const data = await res.json();
      setUsers(data);
    } catch (err) {
    }
  };

  useEffect(() => {
    fetchProperty();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (property) {
      localStorage.setItem('property', JSON.stringify(property));
    }
  }, [property]);

  const handleSave = async (formData) => {
    const data = new FormData();
    for (const [key, value] of Object.entries(formData)) {
      if (key === 'logo' && value instanceof File) {
        data.append('logo', value);
      }
      if (key === 'featureImage' && value instanceof File) {
        data.append('featureImage', value);
      }
      else if (value !== undefined && value !== null) {
        const finalValue = typeof value === 'object' ? JSON.stringify(value) : value;
        data.append(key, finalValue);
      }
    }
  
    const user = JSON.parse(localStorage.getItem('user'));
    const propertyId = user?.propertyId?.id || user?.properties?.[0]?.propertyId?.id;
    if (!propertyId) {
      return;
    }
    data.append('propertyId', propertyId);
  
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        body: data
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to save');
    } catch (err) {
    }
  
    fetchProperty();
    setOpen(false);
  };

  const handleUserSave = async (userData) => {
    const url = currentUser
      ? `${API_URL}/users/${currentUser._id}`
      : `${API_URL}/users`;
    const method = currentUser ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });
      const result = await res.json();
      if (!res.ok) {
        throw result; // 🔥 propagate error back to UserForm
      }
    } catch (error) {
      throw error; // ensure UserForm catches it
    }

    fetchUsers();
    setUserDialogOpen(false);
  };

  const handleUserEdit = (user) => {
    setCurrentUser(user);
    setUserDialogOpen(true);
  };

  const handleUserDelete = async (user) => {
    await fetch(`${API_URL}/users/${user._id}`, {
      method: 'DELETE'
    });
    fetchUsers();
  };

  const handleLogoDelete = async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    const propertyId = user?.propertyId?.id || user?.properties?.[0]?.propertyId?.id;
    if (!propertyId) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/logo?propertyId=${propertyId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        fetchProperty();
      }
    } catch (err) {
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('socialLinks.')) {
      const key = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        socialLinks: {
          ...prev.socialLinks,
          [key]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const allowedFeatures = JSON.parse(localStorage.getItem('allowedFeatures') || '[]');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user?.role === 'admin';

  return (
    <Box display="flex">
      <Box
        className="sidebar-container"
        sx={{
          display: 'flex',
          position: 'relative',
          minHeight: '100vh'
        }}
      >
        <Button
          onClick={() => setSidebarExpanded(prev => !prev)}
          sx={{
            position: 'absolute',
            top: 0,
            left: sidebarExpanded ? 220 : 32,
            transform: 'translateX(-50%)',
            zIndex: 20,
            minWidth: 24,
            padding: 0,
            fontSize: '1.25rem',
            fontWeight: 'bold',
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: '50%',
            width: 28,
            height: 28,
            lineHeight: 1,
            '&:hover': {
              backgroundColor: '#f0f0f0'
            }
          }}
        >
          {sidebarExpanded ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#A58E63" viewBox="0 0 24 24">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#A58E63" viewBox="0 0 24 24">
              <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
            </svg>
          )}
        </Button>
        <Box
          sx={{
            background: '#f5f5f5',
            width: sidebarExpanded ? 220 : 32,
            padding: sidebarExpanded ? '16px 12px' : '16px 0px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            gap: '20px',
            height: '100%',
            borderRight: '1px solid #ddd',
            transition: 'all 0.3s ease'
          }}
        >
          {sidebarExpanded && (
            <List>
              <ListItem
                button
                selected={section === 'propertyInfo'}
                onClick={() => setSection('propertyInfo')}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 2,
                  mb: 1,
                  px: 2,
                  py: 1.5,
                  bgcolor: section === 'propertyInfo' ? '#e6f0ff' : 'transparent',
                  '&:hover': {
                    bgcolor: '#f0f4ff'
                  }
                }}
              >
                <ListItemText primary="Property Info" />
              </ListItem>
              <ListItem
                button
                selected={section === 'reviews'}
                onClick={() => setSection('reviews')}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 2,
                  mb: 1,
                  px: 2,
                  py: 1.5,
                  bgcolor: section === 'reviews' ? '#e6f0ff' : 'transparent',
                  '&:hover': {
                    bgcolor: '#f0f4ff'
                  }
                }}
              >
                <ListItemText primary="Reviews" />
              </ListItem>
              <ListItem
                button
                selected={section === 'analytics'}
                onClick={() => setSection('analytics')}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 2,
                  mb: 1,
                  px: 2,
                  py: 1.5,
                  bgcolor: section === 'analytics' ? '#e6f0ff' : 'transparent',
                  '&:hover': {
                    bgcolor: '#f0f4ff'
                  }
                }}
              >
                <ListItemText primary="Analytics" />
              </ListItem>
            </List>
          )}
        </Box>
      </Box>

      <Box flexGrow={1} p={2}>
        {section === 'propertyInfo' && (
          <>
            {(isAdmin || allowedFeatures?.property?.editPropertyInfo) && (
              <Button variant="contained" onClick={() => setOpen(true)}>Edit Property Info</Button>
            )}
            {!property ? (
              <Box sx={{ mt: 2 }}>
                <SkeletonBlock />
                <SkeletonBlock />
                <SkeletonBlock />
              </Box>
            ) : (
              <PropertyCard
                property={{
                  ...property,
                  address: property.address,
                  email: property.email,
                  phone: property.phone,
                  description: property.description,
                  googleMapEmbed: property.googleMapEmbed,
                  googleMapLink: property.googleMapLink,
                  logo: property.logo,
                  socialLinks: {
                    facebook: property.socialLinks?.facebook,
                    instagram: property.socialLinks?.instagram,
                    website: property.socialLinks?.website,
                    whatsapp: property.socialLinks?.whatsapp
                  },
                  images: property.images,
                  featureImage: property.featureImage
                }}
              />
            )}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
              <PropertyForm initialData={property || {}} onSave={handleSave} onCancel={() => setOpen(false)} />
            </Dialog>
          </>
        )}

      

        {section === 'reviews' && (
          <ReviewPanel />
        )}
        {section === 'analytics' && (
          <AnalyticsDashboard />
        )}
      </Box>
    </Box>
  );
}